import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { t } from 'i18next';
import { useState } from 'react';

import activityKindPresentation, {
  REASONING_ICON as ReasoningIcon,
} from '~tread-agent/trace/activityKindPresentation';
import TraceReasoningRow from '~tread-agent/trace/TraceReasoningRow';
import { persistedTraceItems } from '~tread-agent/trace/traceUtils';
import { TreadAgentTraceItem } from '~tread-agent/types';

const MARKER_COLUMN_WIDTH_IN_PX = 16;

function TraceTimelineRow({
  isLast,
  item,
}: {
  isLast: boolean;
  item: TreadAgentTraceItem;
}) {
  const theme = useTheme();
  const { Icon, label } =
    item.kind === 'step'
      ? activityKindPresentation(item.activityKind)
      : { Icon: ReasoningIcon, label: t('tread_agent.trace.thinking') };

  return (
    <Box sx={{ display: 'flex', gap: 0.75, minWidth: 0 }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          width: `${MARKER_COLUMN_WIDTH_IN_PX}px`,
        }}
      >
        <Icon
          aria-label={label}
          role="img"
          sx={{
            color: theme.brandV2.colors.treadGray3,
            fontSize: 16,
            mt: 0.25,
          }}
        />

        {!isLast && (
          <Box
            sx={{
              borderLeft: `1px solid ${theme.brandV2.colors.treadGray7}`,
              flexGrow: 1,
              my: 0.25,
            }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0, pb: isLast ? 0 : 1, width: '100%' }}>
        {item.kind === 'reasoning' ? (
          <TraceReasoningRow text={item.text} />
        ) : (
          <Typography
            sx={{
              color: theme.brandV2.colors.treadGray2,
              fontSize: '12px',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.activity ?? label}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

interface TurnTraceDisclosureProps {
  elapsedSeconds: number;
  trace: TreadAgentTraceItem[];
}

export default function TurnTraceDisclosure({
  elapsedSeconds,
  trace,
}: TurnTraceDisclosureProps) {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [isOpen, setIsOpen] = useState(false);

  const items = persistedTraceItems(trace);

  if (items.length === 0) {
    return null;
  }

  const Caret = isOpen ? KeyboardArrowDownRounded : KeyboardArrowRightRounded;
  const summary = t('tread_agent.trace.worked_for', {
    seconds: Math.round(elapsedSeconds),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <ButtonBase
        aria-expanded={isOpen}
        aria-label={
          isOpen ? t('tread_agent.trace.hide_work') : t('tread_agent.trace.show_work')
        }
        data-test-id="tread-agent-trace-disclosure"
        onClick={() => setIsOpen((wasOpen) => !wasOpen)}
        sx={{
          alignSelf: 'flex-start',
          borderRadius: theme.brandV2.borderRadius,
          gap: 0.25,
          py: 0.25,
          '&:hover .trace-summary': { color: theme.brandV2.colors.treadGray1 },
        }}
      >
        <Typography
          className="trace-summary"
          sx={{
            color: theme.brandV2.colors.treadGray2,
            fontSize: '12px',
            fontWeight: 500,
            transition: 'color 0.15s ease',
          }}
        >
          {summary}
        </Typography>

        <Caret sx={{ color: theme.brandV2.colors.treadGray3, fontSize: 16 }} />
      </ButtonBase>

      <Collapse in={isOpen} timeout={prefersReducedMotion ? 0 : 'auto'} unmountOnExit>
        <Box
          data-test-id="tread-agent-trace-expanded"
          sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}
        >
          {items.map((item, index) => (
            <TraceTimelineRow
              key={item.id}
              isLast={index === items.length - 1}
              item={item}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
